// SIL-1328: render the server's tier-tagged price. The client never computes
// a range — it only formats what the API already decided to send.
import type { AskPricing, PriceRange, UnverifiedPricing, VerifiedPricing } from '../../dto/marketplace.js';

// Explicit min AND max fraction digits — with only maximumFractionDigits set,
// ICU's default minimumFractionDigits for 'currency' style (2, from USD's
// currency-digits table) wins on some Node/ICU builds, producing "$22.50K"
// instead of "$22.5K" on the very same input other builds trim correctly.
// Pinning both makes the output identical across Node versions (CI vs local
// — see SIL-1328 PR #2046 CI failure, ICU-version-dependent trailing zero).
const compact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const exact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatPriceRange(r: PriceRange): string {
  return `${compact.format(r.lo)} - ${compact.format(r.hi)}`;
}

export function priceText(p: VerifiedPricing | UnverifiedPricing | AskPricing): string {
  // SIL-1480: 'ask' mode — one exact price, same formatting as the verified
  // tier's exact per-unit figure.
  if (p.tier === 'ask') return exact.format(p.priceUsd);
  // SIL-1578: a lot with an unknown GPU count has no per-GPU figure; fall back
  // to the per-node one rather than rendering a fake or empty value.
  if (p.tier === 'verified') {
    return exact.format(p.pricePerGpuUsd ?? p.pricePerNodeUsd);
  }
  return formatPriceRange(p.priceRange.perGpu ?? p.priceRange.perNode);
}
